<template>
  <div class="dod-container" v-if="!loading">
    <settlement-status-counters :counters="settlementCounters" />

    <div class="toolbar">
      <custom-button
        v-if="panelCan('showFinishOrdersInApp')"
        :customStyles="'height: 44px;padding: 0;margin:0; width: fit-content; padding: 0 1rem'"
        :size="'small'"
        :title="
          loadingPreview
            ? 'Sprawdzam dane...'
            : loadingFinishingOrders
            ? 'Finalizuję...'
            : 'Zakończ zlecenia w SeniorApp'
        "
        :gradient="true"
        :disabled="loadingFinishingOrders || loadingPreview"
        @click="finishOrdersInSeniorApp"
      />
      <dod-search-input
        class="search"
        :with-filters="true"
        :filter-settings="[
          'SETTLEMENT_STATUS',
          'EMPLOYEE_STATUS',
          'CUSTOMER_STATUS',
          'DEPARTMENT',
          'PRIORITY_STATUS',
          'SCOPE_OF_CARE',
        ]"
        @search="handleSearch"
        @onFilter="handleFilters"
        @onMonthChange="onMonthChange"
        :date="date"
        :withMonthSwitcher="true"
      />
    </div>

    <pagination-arrows
      v-if="employees.meta && employees.meta.total"
      :onPage="employees.meta.per_page"
      :pageNumber="employees.meta.current_page"
      :total="employees.meta.total"
      @nextPage="handleNextPage"
      @previousPage="handlePreviousPage"
    />

    <loader v-if="loadingData" />

    <table
      class="table table-striped"
      v-else-if="employees && employees.data && employees.data.length > 0"
    >
      <thead>
        <tr>
          <td>ID</td>
          <td>Opiekun</td>
          <td>Adres</td>
          <td>Numer telefonu</td>
          <td>Dni</td>
          <td>Ilość godz. zaplanowanych</td>
          <td>Ilość godz. wykonanych</td>
          <td>Status rozliczenia</td>
          <td class="summary-cell">
            <span class="cell-center">
              <custom-button
                :customStyles="'height: 32px; width: max-content; margin-top: 0'"
                :size="'small'"
                :title="exporting ? 'Generowanie...' : 'Pobierz zestawienie'"
                :disabled="exporting"
                @click="downloadSummary"
              />
            </span>
          </td>
        </tr>
      </thead>
      <tbody>
        <tr v-for="employee in employees.data" :key="employee.id">
          <td>{{ employee.id }}</td>
          <td>
            {{ employee.firstName }} {{ employee.lastName }}
            <span
              v-if="employee.mainAppUserId && panelCan('showMainAppLinking')"
              class="seniorapp-label"
              >{{ $mainAppBadge }}</span
            >
          </td>
          <td>{{ employee.address }}</td>
          <td>{{ employee.phone }}</td>
          <td>{{ employee.days }}</td>
          <td>{{ employee.numberOfHours }}</td>
          <td>{{ employee.numberOfMadeHours || 0 }}</td>
          <td>
            <settlement-status-badge :status="employee.settlementStatus" />
          </td>
          <td>
            <span class="cell-center">
              <gradient-button
                @handleClick="openSettlement(employee)"
                :title="settlementAction(employee).label"
                :customStyles="'width: 6vw; font-size: calc(11px + 0.05vw);'"
                :disabled="settlementAction(employee).disabled"
              />
            </span>
          </td>
        </tr>
      </tbody>
    </table>

    <dod-empty-list v-else />

    <settlement-modal
      :visible="settlementModalVisible"
      :employee-id="settlementEmployeeId"
      :month="Number(month)"
      :year="Number(year)"
      :mode="settlementMode"
      @close="settlementModalVisible = false"
      @changed="onSettlementChanged"
    />

    <custom-modal
      v-if="panelCan('showFinishOrdersInApp')"
      :title="`Zatwierdzanie i zakończenie zleceń w SeniorApp - ${monthLabel} ${year}`"
      :id="'finish-orders'"
      :hideDefaultFooter="true"
    >
      <template #content>
        <table class="table table-striped finish-preview" v-if="finishPreviewRows.length">
          <thead>
            <tr>
              <td class="lp">L.p.</td>
              <td>Imię i nazwisko</td>
              <td>Numer tel.</td>
              <td class="numeric">Stawka</td>
              <td class="numeric">Il. godzin zrealizowanych</td>
              <td class="numeric">Kwota brutto</td>
              <td class="numeric">Prowizja</td>
              <td class="numeric">Kwota netto</td>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in finishPreviewRows"
              :key="row.mainAppUserId || row.dodEmployeeId || index"
              :class="{ 'has-warnings': rowHasWarnings(row) }"
            >
              <td class="lp">{{ index + 1 }}</td>
              <td>
                {{ row.employeeName }}
                <div v-if="rowHasWarnings(row)" class="warnings">
                  <span v-for="code in row.warnings" :key="code" class="warning">
                    {{ warningLabel(code) }}
                  </span>
                </div>
              </td>
              <td>{{ row.phone }}</td>
              <td class="numeric">{{ formatAmount(row.hourlyPrice) }} zł</td>
              <td class="numeric">{{ formatHours(row.madeHours) }}</td>
              <td class="numeric">{{ formatAmount(row.grossAmount) }} zł</td>
              <td class="numeric">{{ formatCommission(row.commission) }}</td>
              <td class="numeric">
                {{ row.netAmount === null ? "—" : `${formatAmount(row.netAmount)} zł` }}
              </td>
            </tr>
          </tbody>
          <tfoot v-if="finishPreviewTotals">
            <tr>
              <td class="lp"></td>
              <td><b>Razem ({{ finishPreviewTotals.employees }} opiekunów)</b></td>
              <td></td>
              <td class="numeric"></td>
              <td class="numeric">
                <b>{{ formatHours(finishPreviewTotals.madeHours) }}</b>
              </td>
              <td class="numeric">
                <b>{{ formatAmount(finishPreviewTotals.grossAmount) }} zł</b>
              </td>
              <td class="numeric">
                <b>{{ formatAmount(finishPreviewTotals.commissionAmount) }} zł</b>
              </td>
              <td class="numeric">
                <b>{{ formatAmount(finishPreviewTotals.netAmount) }} zł</b>
              </td>
            </tr>
          </tfoot>
        </table>
        <p v-else class="no-rows">
          Brak opiekunów z godzinami do rozliczenia w tym miesiącu.
        </p>

        <p v-if="settlementMismatch" class="mismatch">
          Uwaga: podsumowanie panelu ({{ formatHours(settlementSummaryForMonth.dnrCompletedHours) }} godz.,
          {{ formatAmount(settlementSummaryForMonth.fundsToWithdrawn) }} zł brutto) różni się od tego, co
          rozliczy SeniorApp. Sprawdź, czy oba systemy patrzą na ten sam miesiąc.
        </p>

        <p class="check-data">Przed zatwierdzeniem sprawdź poprawność danych!</p>

        <div class="buttons">
          <custom-button
            title="Wstecz"
            :gradient="false"
            :gray="true"
            style="width: 50%"
            @click="closeFinishOrdersInSeniorApp"
            :size="'small'"
          />
          <custom-button
            :title="
              loadingFinishingOrders ? 'Finalizuję...' : 'Zatwierdź i zakończ zlecenia'
            "
            :gradient="true"
            style="width: 50%"
            @click="confirmFinishOrdersInSeniorApp"
            :size="'small'"
            :disabled="loadingFinishingOrders || !finishPreviewRows.length"
          />
        </div>
      </template>
    </custom-modal>
    <button
      :id="`finish-orders-open`"
      hidden
      data-toggle="modal"
      data-target="#finish-orders"
    />
  </div>
</template>

<script>
import DodSearchInput from "../../components/dod/DodSearchInput.vue";
import moment from "moment";
import GradientButton from "../../components/dod/GradientButton.vue";
import DodEmptyList from "../../components/dod/DodEmptyList.vue";
import CustomModal from "../../components/CustomModal.vue";
import SettlementModal from "../../components/dod/settlement/SettlementModal.vue";
import SettlementStatusBadge from "../../components/dod/settlement/SettlementStatusBadge.vue";
import SettlementStatusCounters from "../../components/dod/settlement/SettlementStatusCounters.vue";
import CustomButton from "../../components/CustomButton.vue";
import PaginationArrows from "../../components/PaginationArrows.vue";
import Loader from "../../components/Loader.vue";
export default {
  components: {
    DodSearchInput,
    GradientButton,
    DodEmptyList,
    CustomModal,
    SettlementModal,
    SettlementStatusBadge,
    SettlementStatusCounters,
    CustomButton,
    PaginationArrows,
    Loader,
  },
  name: "DodSettlementsList",
  data() {
    return {
      loading: true,
      loadingData: false,
      loadingFinishingOrders: false,
      loadingPreview: false,
      month: "",
      year: "",
      filters: {},
      search: "",
      date: "",
      limit: 20,
      page: 1,
      settlementModalVisible: false,
      settlementEmployeeId: null,
      settlementMode: "manager",
      exporting: false,
    };
  },

  async mounted() {
    const now = moment();
    // Deep-link z powiadomienia: ?month&year ustawia wyświetlany miesiąc.
    const queryMonth = Number(this.$route.query.month);
    const queryYear = Number(this.$route.query.year);
    if (queryMonth && queryYear) {
      const linked = moment().year(queryYear).month(queryMonth - 1).date(1);
      this.date = linked.toDate();
      this.month = queryMonth;
      this.year = queryYear;
    } else {
      this.date = now.toDate();
      this.month = now.get("month") + 1;
      this.year = now.get("year");
    }
    await Promise.all([this.fetchEmployees(), this.fetchCounters()]);
    await this.$store.dispatch("fetchDodAppSettingsByName", "CHANGE_BLOCK_DATE");
    this.loading = false;
  },

  methods: {
    // Cały miesiąc (wszystkie strony) z bieżącym wyszukiwaniem i filtrami - to samo co tabela.
    async downloadSummary() {
      this.exporting = true;
      try {
        const response = await this.$store.dispatch("generateSettlementsXlsxReport", {
          month: this.month,
          year: this.year,
          search: this.search,
          filters: this.filters,
        });
        const fileURL = window.URL.createObjectURL(new Blob([response]));
        const fileLink = document.createElement("a");
        fileLink.href = fileURL;
        fileLink.setAttribute(
          "download",
          `zestawienie_rozliczen_${this.year}_${String(this.month).padStart(2, "0")}.xlsx`
        );
        document.body.appendChild(fileLink);
        fileLink.click();
        document.body.removeChild(fileLink);
        window.URL.revokeObjectURL(fileURL);
      } catch (error) {
        console.log(error);
        this.$notify({
          group: "error",
          text: "Nie udało się wygenerować zestawienia",
        });
      } finally {
        this.exporting = false;
      }
    },
    async fetchCounters() {
      try {
        await this.$store.dispatch("fetchSettlementCounters", {
          month: this.month,
          year: this.year,
        });
      } catch (error) {
        console.log(error);
      }
    },
    // A settlement changes a status, so the bar above the table has to move with it.
    async onSettlementChanged() {
      await Promise.all([this.fetchEmployees(), this.fetchCounters()]);
    },
    async handleNextPage() {
      this.page++;
      await this.fetchEmployees();
    },
    async handlePreviousPage() {
      this.page--;
      await this.fetchEmployees();
    },

    // Modal otwieramy dopiero z pobranym podgladem - inaczej admin zobaczylby puste
    // albo poprzednie liczby i zatwierdzil w ciemno.
    async finishOrdersInSeniorApp() {
      if (this.loadingFinishingOrders || this.loadingPreview) return;
      try {
        this.loadingPreview = true;
        await this.$store.dispatch("previewFinishOrdersInSeniorApp", {
          month: this.month,
          year: this.year,
        });
        let btnToOpen = window.document.getElementById("finish-orders-open");
        if (btnToOpen) btnToOpen.click();
      } catch (error) {
        console.log(error);
        const text =
          error && error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : `Nie udało się pobrać podsumowania do zatwierdzenia`;
        this.$notify({ text, group: "error" });
      } finally {
        this.loadingPreview = false;
      }
    },
    rowHasWarnings(row) {
      return !!(row && row.warnings && row.warnings.length);
    },
    warningLabel(code) {
      const labels = {
        USER_NOT_FOUND: "Brak konta w SeniorApp",
        NAME_MISMATCH: "Inne imię i nazwisko na koncie",
        PHONE_MISMATCH: "Inny numer telefonu na koncie",
        NO_ACTIVE_DNR_SERVICE: "Brak usługi DNR - zostanie utworzona",
        ZERO_HOURLY_PRICE: "Stawka 0 zł",
        ZERO_MADE_HOURS: "Brak godzin",
        COMMISSION_EXPIRED: "Prowizja z konta wygasła - naliczy się domyślna",
        COMMISSION_IS_SYSTEM_DEFAULT: "Prowizja domyślna, nie indywidualna",
        ORDER_MISSING_IN_APP: "Zlecenie nie istnieje w SeniorApp - zostanie utworzone",
        ORDER_NOT_ACCEPTED: "Zlecenie nie zostanie rozliczone",
        ORDER_ALREADY_SETTLED: "Zlecenie już zostało rozliczone",
        ORDER_UNREALIZED:
          "Zlecenie usunięte w panelu DNR - nie zostanie rozliczone",
      };
      return labels[code] || code;
    },
    formatAmount(value) {
      const number = Number(value);
      if (value === null || value === undefined || isNaN(number)) return "—";
      return number.toLocaleString("pl-PL", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatHours(value) {
      const number = Number(value);
      if (value === null || value === undefined || isNaN(number)) return "—";
      return Number.isInteger(number)
        ? String(number)
        : number.toLocaleString("pl-PL", { maximumFractionDigits: 2 });
    },
    formatCommission(value) {
      const number = Number(value);
      if (value === null || value === undefined || isNaN(number)) return "—";
      return `${(number * 100).toLocaleString("pl-PL", { maximumFractionDigits: 2 })}%`;
    },
    async confirmFinishOrdersInSeniorApp() {
      if (this.loadingFinishingOrders) return;
      try {
        this.loadingFinishingOrders = true;
        const response = await this.$store.dispatch("finishOrdersInSeniorApp", {
          month: this.month,
          year: this.year,
        });
        if (response) {
          this.$notify({
            text: `Zakończono zlecenia`,
            group: "success",
          });
        }
        this.closeFinishOrdersInSeniorApp();
      } catch (error) {
        console.log(error);
        const text =
          error && error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : `Nie udało się zakończyć zleceń`;
        this.$notify({ text, group: "error" });
      } finally {
        this.loadingFinishingOrders = false;
      }
    },
    closeFinishOrdersInSeniorApp() {
      let btnToOpen = window.document.getElementById("finish-orders-close");
      if (btnToOpen) btnToOpen.click();
    },

    async fetchEmployees() {
      await this.$store.dispatch("getDodEmployeesInMonth", {
        search: this.search,
        filters: this.filters,
        month: this.month,
        year: this.year,
        limit: this.limit,
        page: this.page,
      });
      await this.$store.dispatch("fetchSettlementSummaryForMonth", {
        month: this.month,
        year: this.year,
      });
    },

    async onMonthChange(date) {
      this.loadingData = true;
      const dateToSet = moment(date);
      this.date = dateToSet.toDate();
      this.month = dateToSet.get("month") + 1;
      this.year = dateToSet.get("year");
      this.page = 1;
      await Promise.all([this.fetchEmployees(), this.fetchCounters()]);
      this.loadingData = false;
    },
    async handleSearch(searchValue) {
      this.search = searchValue;
      this.page = 1;
      this.loadingData = true;
      await this.fetchEmployees();
      this.loadingData = false;
    },
    async handleFilters(filters) {
      this.filters = {};
      if (filters) {
        filters.forEach((filter) => (this.filters[filter.key] = filter.value));
      }
      this.page = 1;
      this.loadingData = true;
      await this.fetchEmployees();
      this.loadingData = false;
    },

    settlementAction(employee) {
      // Miesiąc zamknięty (data <= blokada zmian) -> brak możliwości edycji.
      const status = employee.settlementStatus || "NIEROZLICZONO";
      const hasHours = !!employee.numberOfHours;
      const closed = this.isMonthClosed;
      if (this.isSettlementAdmin) {
        if (status === "DO_WERYFIKACJI")
          return { label: "Zweryfikuj", mode: "manager", disabled: closed || !hasHours };
        if (status === "ROZLICZONO")
          return { label: "Edytuj", mode: "manager", disabled: closed };
        return { label: "Rozlicz", mode: "manager", disabled: closed || !hasHours };
      }
      // Pracownik
      if (status === "DO_WERYFIKACJI" || status === "ROZLICZONO")
        return { label: "Edytuj", mode: "employee", disabled: true };
      const label = status === "NIEROZLICZONO" ? "Przygotuj" : "Edytuj";
      return { label, mode: "employee", disabled: closed || !hasHours };
    },
    openSettlement(employee) {
      const action = this.settlementAction(employee);
      if (action.disabled) return;
      this.settlementEmployeeId = employee.id;
      this.settlementMode = action.mode;
      this.settlementModalVisible = true;
    },
  },
  computed: {
    isSettlementAdmin() {
      return this.$store.getters.getIsSettlementAdmin;
    },
    blockChangesDate() {
      const settings = this.$store.getters.getDODAppSettings;
      return settings && settings.value ? settings.value : null;
    },
    // Miesiąc jest zamknięty, gdy jego ostatni dzień jest w dacie blokady lub przed nią.
    isMonthClosed() {
      if (!this.blockChangesDate) return false;
      const endOfMonth = moment()
        .year(this.year)
        .month(this.month - 1)
        .endOf("month");
      return moment(this.blockChangesDate).endOf("day").isSameOrAfter(endOfMonth);
    },
    settlementSummaryForMonth() {
      return this.$store.getters.getSettlementSummaryForMonth;
    },
    dnrFinishPreview() {
      return this.$store.getters.getDnrFinishPreview;
    },
    finishPreviewRows() {
      return (this.dnrFinishPreview && this.dnrFinishPreview.rows) || [];
    },
    finishPreviewTotals() {
      return (this.dnrFinishPreview && this.dnrFinishPreview.totals) || null;
    },
    monthLabel() {
      const months = [
        "styczeń", "luty", "marzec", "kwiecień", "maj", "czerwiec",
        "lipiec", "sierpień", "wrzesień", "październik", "listopad", "grudzień",
      ];
      return months[this.month - 1] || this.month;
    },
    // Panel i SeniorApp licza godziny i brutto niezaleznie. Rozjazd znaczy, ze systemy nie
    // patrza na ten sam miesiac - lepiej pokazac to przed zatwierdzeniem niz po wyplacie.
    settlementMismatch() {
      const summary = this.settlementSummaryForMonth;
      const totals = this.finishPreviewTotals;
      if (!summary || !totals) return false;
      const hoursDiffer =
        Math.abs(Number(summary.dnrCompletedHours || 0) - Number(totals.madeHours || 0)) > 0.01;
      const grossDiffer =
        Math.abs(Number(summary.fundsToWithdrawn || 0) - Number(totals.grossAmount || 0)) > 0.01;
      return hoursDiffer || grossDiffer;
    },
    employees() {
      return this.$store.getters.getDodEmployeesInMonth;
    },
    settlementCounters() {
      return this.$store.getters.getSettlementCounters;
    },
  },
};
</script>

<style scoped>
table {
  table-layout: fixed;
  font-size: calc(11px + 0.05vw);
}
.toolbar {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
}
/* The search input carries its own bottom margin, which pushed the centred button down. */
.toolbar .search {
  flex: 1;
  margin-bottom: 0;
}
/* Global td rules force text-align and zero horizontal padding/margin of any div with
   !important: a span (not targeted) aligns the button and its padding lives on the inner <p>. */
.summary-cell {
  width: 210px;
}
/* Header and row buttons share one centre line in the last column. */
.cell-center {
  display: flex;
  justify-content: center;
}
.cell-center .gradient-button {
  flex: none;
}
.summary-cell >>> p {
  white-space: nowrap;
  padding: 0 16px;
  margin: 0;
}
.buttons {
  display: flex;
  width: 100%;
  gap: 1rem;
}
.finish-preview {
  table-layout: auto;
}
.finish-preview .lp {
  width: 3rem;
}
.finish-preview .numeric {
  text-align: right;
  white-space: nowrap;
}
.finish-preview tr.has-warnings {
  background-color: #fff6e5;
}
.finish-preview .warnings {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.25rem;
}
.finish-preview .warning {
  font-size: calc(9px + 0.05vw);
  line-height: 1.3;
  padding: 0.1rem 0.35rem;
  border: 1px solid #c47f17;
  border-radius: 2px;
  color: #8a5d00;
}
.finish-preview tfoot td {
  border-top: 2px solid #333;
}
.check-data {
  text-align: right;
  font-weight: 600;
  margin: 1rem 0;
}
.mismatch {
  color: #8a5d00;
  background-color: #fff6e5;
  border-left: 3px solid #c47f17;
  padding: 0.5rem 0.75rem;
  margin-top: 1rem;
}
.no-rows {
  margin: 1.5rem 0;
}
</style>
